import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, ShieldCheck, AlertTriangle, Lock } from 'lucide-react'
import { loadPayPal, type PayPalNamespace } from '../../lib/paypal'
import { formatCents } from '../../lib/money'
import type { Locale, PayPalConfig } from '../../lib/api/types'

interface BaseProps {
  config: PayPalConfig
  locale: Locale
  amountCents: number
  /** Open a DRAFT reservation + PayPal order; resolve with the PayPal order id. */
  createOrder: () => Promise<string>
  /** Capture the approved order and finalize the reservation. */
  onApprove: (orderID: string) => Promise<void>
  onError?: (e: unknown) => void
  /** Preview only — render the UI without loading the SDK or hitting the API. */
  preview?: boolean
}

// ── Shared SDK loader ──────────────────────────────────────────────────────
function usePayPalSdk(config: PayPalConfig, locale: Locale) {
  const [state, setState] = useState<{ paypal: PayPalNamespace | null; status: 'loading' | 'ready' | 'error' }>({
    paypal: null,
    status: 'loading',
  })
  useEffect(() => {
    if (!config.clientId) {
      setState({ paypal: null, status: 'error' })
      return
    }
    let cancelled = false
    loadPayPal(config.clientId, { currency: config.currency, intent: config.intent, locale })
      .then((paypal) => !cancelled && setState({ paypal, status: 'ready' }))
      .catch(() => !cancelled && setState({ paypal: null, status: 'error' }))
    return () => {
      cancelled = true
    }
  }, [config.clientId, config.currency, config.intent, locale])
  return state
}

function ErrorNote() {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4">
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-rose-500" />
      <div>
        <p className="font-display text-sm font-bold text-rose-900">{t('summary.pay.unavailableTitle')}</p>
        <p className="mt-0.5 text-sm text-rose-700">{t('summary.pay.unavailableText')}</p>
      </div>
    </div>
  )
}

function SecureNote() {
  const { t } = useTranslation()
  return (
    <p className="flex items-center justify-center gap-1.5 text-xs text-ink-400">
      <ShieldCheck className="h-3.5 w-3.5 text-brand-500" /> {t('summary.pay.secure')}
    </p>
  )
}

// ── PayPal + Pay Later buttons (no inline card; card has its own method) ────
export function PayPalButtons({ config, locale, amountCents, createOrder, onApprove, onError, preview }: BaseProps) {
  const { t } = useTranslation()
  const createOrderRef = useRef(createOrder)
  const onApproveRef = useRef(onApprove)
  const onErrorRef = useRef(onError)
  createOrderRef.current = createOrder
  onApproveRef.current = onApprove
  onErrorRef.current = onError

  const { paypal, status } = usePayPalSdk(config, locale)
  const btnRef = useRef<HTMLDivElement>(null)
  const msgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (preview || status !== 'ready' || !paypal) return
    const instances: Array<{ close?: () => void }> = []
    const hosts = [btnRef, msgRef]
    for (const r of hosts) if (r.current) r.current.innerHTML = ''
    // Render PayPal + Pay Later as explicit funding sources so the card button never shows here
    // (Carta is its own method). Each button needs its own child slot.
    const fundingSources = [paypal.FUNDING?.PAYPAL, paypal.FUNDING?.PAYLATER].filter(Boolean) as string[]
    for (const fundingSource of fundingSources) {
      try {
        const b = paypal.Buttons({
          fundingSource,
          style: { shape: 'rect', color: 'gold', height: 48 },
          createOrder: () => createOrderRef.current(),
          onApprove: (data: { orderID: string }) => onApproveRef.current(data.orderID),
          onError: (e: unknown) => onErrorRef.current?.(e),
        })
        if ((!b.isEligible || b.isEligible()) && btnRef.current) {
          const slot = document.createElement('div')
          slot.className = 'mb-2 last:mb-0'
          btnRef.current.appendChild(slot)
          b.render(slot)
          instances.push(b)
        }
      } catch (e) {
        /* funding source not eligible */
      }
    }
    try {
      if (paypal.Messages && msgRef.current)
        paypal.Messages({ amount: (amountCents / 100).toFixed(2), currency: config.currency, style: { layout: 'text', logo: { type: 'inline' } } }).render(msgRef.current)
    } catch (e) {
      /* optional */
    }
    return () => {
      for (const b of instances) {
        try {
          b.close?.()
        } catch (e) {
          /* ignore */
        }
      }
      for (const r of hosts) if (r.current) r.current.innerHTML = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypal, status, preview])

  if (preview)
    return (
      <div className="space-y-2">
        <div className="flex h-11 items-center justify-center rounded-md bg-[#ffc439] font-display text-base font-bold italic text-[#003087]">PayPal</div>
        <div className="flex h-11 items-center justify-center gap-1.5 rounded-md bg-[#ffc439] text-sm font-semibold text-[#003087]">
          <span className="font-bold italic">PayPal</span> Paga a rate
        </div>
        <SecureNote />
      </div>
    )
  if (status === 'error') return <ErrorNote />
  return (
    <div className="space-y-3">
      {status === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-ink-400">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('summary.pay.loading')}
        </div>
      )}
      <div ref={btnRef} className={status === 'ready' ? '' : 'hidden'} />
      <div ref={msgRef} className={status === 'ready' ? 'text-center' : 'hidden'} />
      <SecureNote />
    </div>
  )
}

// ── Inline card fields (Advanced Card Processing) ──────────────────────────
// PayPal renders its own bordered input inside the iframe — we only set the text style and
// DON'T add our own box (that caused a double border).
const CARD_FIELD_STYLE = {
  input: { 'font-size': '16px', 'font-family': 'inherit', color: '#1c1c28' },
  '.invalid': { color: '#e11d48' },
}
const FIELD_BOX = 'pp-card-field'

export function PayPalCardForm({ config, locale, amountCents, createOrder, onApprove, onError, preview }: BaseProps) {
  const { t } = useTranslation()
  const createOrderRef = useRef(createOrder)
  const onApproveRef = useRef(onApprove)
  const onErrorRef = useRef(onError)
  createOrderRef.current = createOrder
  onApproveRef.current = onApprove
  onErrorRef.current = onError

  const { paypal, status } = usePayPalSdk(config, locale)
  const [eligible, setEligible] = useState(false)
  const [paying, setPaying] = useState(false)
  const nameRef = useRef<HTMLDivElement>(null)
  const numberRef = useRef<HTMLDivElement>(null)
  const expiryRef = useRef<HTMLDivElement>(null)
  const cvvRef = useRef<HTMLDivElement>(null)
  const cardFieldsRef = useRef<ReturnType<NonNullable<PayPalNamespace['CardFields']>> | null>(null)

  useEffect(() => {
    if (preview || status !== 'ready' || !paypal) return
    const hosts = [nameRef, numberRef, expiryRef, cvvRef]
    for (const r of hosts) if (r.current) r.current.innerHTML = ''
    try {
      const cardFields = paypal.CardFields({
        createOrder: () => createOrderRef.current(),
        onApprove: (data: { orderID: string }) => onApproveRef.current(data.orderID),
        onError: (e: unknown) => onErrorRef.current?.(e),
        style: CARD_FIELD_STYLE,
      })
      if (cardFields.isEligible()) {
        cardFieldsRef.current = cardFields
        setEligible(true)
        if (numberRef.current) cardFields.NumberField().render(numberRef.current)
        if (expiryRef.current) cardFields.ExpiryField().render(expiryRef.current)
        if (cvvRef.current) cardFields.CVVField().render(cvvRef.current)
        if (nameRef.current) cardFields.NameField({ placeholder: t('summary.pay.cardName') }).render(nameRef.current)
      }
    } catch (e) {
      /* advanced card processing not enabled */
    }
    return () => {
      for (const r of hosts) if (r.current) r.current.innerHTML = ''
      cardFieldsRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypal, status, preview])

  async function submitCard() {
    if (!cardFieldsRef.current || paying) return
    setPaying(true)
    try {
      await cardFieldsRef.current.submit()
    } catch (e) {
      onErrorRef.current?.(e)
    } finally {
      setPaying(false)
    }
  }

  if (preview)
    return (
      <div className="space-y-3">
        <PreviewField label={t('summary.pay.cardName')}>{t('summary.pay.cardName')}</PreviewField>
        <PreviewField label={t('summary.pay.cardNumber')}>•••• •••• •••• ••••</PreviewField>
        <div className="grid grid-cols-2 gap-3">
          <PreviewField label={t('summary.pay.cardExpiry')}>MM / YY</PreviewField>
          <PreviewField label={t('summary.pay.cardCvv')}>CVV</PreviewField>
        </div>
        <PayButton label={t('summary.pay.payCard', { amount: formatCents(amountCents, locale) })} muted />
        <SecureNote />
      </div>
    )
  if (status === 'error') return <ErrorNote />
  return (
    <div className="space-y-3">
      {status === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-ink-400">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('summary.pay.loading')}
        </div>
      )}
      <div className={eligible ? 'space-y-3' : 'hidden'}>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-500">{t('summary.pay.cardName')}</label>
          <div ref={nameRef} className={FIELD_BOX} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-500">{t('summary.pay.cardNumber')}</label>
          <div ref={numberRef} className={FIELD_BOX} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-500">{t('summary.pay.cardExpiry')}</label>
            <div ref={expiryRef} className={FIELD_BOX} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-500">{t('summary.pay.cardCvv')}</label>
            <div ref={cvvRef} className={FIELD_BOX} />
          </div>
        </div>
        <button
          type="button"
          onClick={submitCard}
          disabled={paying}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-ink-900 px-4 py-3 font-display text-sm font-bold text-white transition hover:bg-ink-800 disabled:opacity-60"
        >
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {t('summary.pay.payCard', { amount: formatCents(amountCents, locale) })}
        </button>
        <SecureNote />
      </div>
    </div>
  )
}

function PreviewField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-ink-500">{label}</label>
      <div className="flex h-11 items-center rounded-xl border border-ink-200 bg-white px-3 text-ink-300">{children}</div>
    </div>
  )
}

function PayButton({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <div className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-display text-sm font-bold text-white ${muted ? 'bg-ink-900/60' : 'bg-ink-900'}`}>
      <Lock className="h-4 w-4" /> {label}
    </div>
  )
}
